import { useState } from "react";
import { confirmDialog } from "@/common/components/ConfirmDialog";
import { AgentEffect } from "@/common/components/AgentEffect";
import { PlaceOptionsMenu } from "./PlaceOptionsMenu";
import { resolveImageUrl } from "../resolveImageUrl";
import { pickRowPhotoUrl } from "../lib/placeFields";
import {
  normalizePlaceAddress,
  formatShortAddress,
} from "../lib/placeAddress";
import { findPlaceFolderLabel } from "../lib/placeMembership";
import { getPlaceCategory } from "../lib/placeCategory";
import type { PlaceObject } from "../types";

interface PlaceFolder {
  id: string;
  label: string;
  objectCount: number | undefined;
}

interface PlaceCardGridProps {
  places: PlaceObject[];
  placeFolders: PlaceFolder[];
  showAllPlaces: boolean;
  categoryFilter: string;
  priceFilter: string;
  isMobile: boolean;
  showOptionsMenu: boolean;
  placeMenuId: string | null;
  showArchivedPlaces: boolean;
  updatePlace: { mutate: (args: any) => void };
  handlePlaceClick: (place: PlaceObject) => void;
  setPlaceMenuId: (v: string | null) => void;
  setAddToCollectionPlaceId: (v: string | null) => void;
  handleDeletePlace: (id: string) => void;
}

const PlacePhotoPlaceholder = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
    stroke="var(--color-text-muted, #bbb)" strokeWidth="1.5">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="m21 15-5-5L5 21" />
  </svg>
);

function PlacePhoto({ url }: { url: string }) {
  const [errored, setErrored] = useState(false);
  if (errored) return <PlacePhotoPlaceholder />;
  return (
    <img
      src={url}
      alt=""
      style={{ width: "100%", height: "100%", objectFit: "cover" }}
      onError={() => setErrored(true)}
    />
  );
}

/**
 * The flat place list ("List view") rendered inside a folder, in "All" mode,
 * or for a standalone place_list module. The category/price filter and the
 * per-row click + kebab-menu actions stay owned by the orchestrator and are
 * threaded through props/callbacks.
 */
export function PlaceCardGrid({
  places,
  placeFolders,
  showAllPlaces,
  categoryFilter,
  priceFilter,
  isMobile,
  showOptionsMenu,
  placeMenuId,
  showArchivedPlaces,
  updatePlace,
  handlePlaceClick,
  setPlaceMenuId,
  setAddToCollectionPlaceId,
  handleDeletePlace,
}: PlaceCardGridProps) {
  return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {places
          .filter((place) => {
            if (
              showAllPlaces &&
              categoryFilter !== "all" &&
              getPlaceCategory(place.primaryType, place) !==
                categoryFilter
            )
              return false;
            if (priceFilter !== "all" && place.priceLevel) {
              const placePrice = (place.priceLevel.match(/\$/g) || [])
                .length;
              const filterPrice = (priceFilter.match(/\$/g) || [])
                .length;
              if (placePrice > filterPrice) return false;
            }
            return true;
          })
          .map((place) => {
            const photoUrl = resolveImageUrl(pickRowPhotoUrl(place));
            const address = normalizePlaceAddress(place);
            const userNotes = place.userNotes;
            const shortAddress = formatShortAddress(address);

            return (
              <div
                key={place.id}
                onClick={() => {
                  if (showOptionsMenu) return;
                  handlePlaceClick(place);
                }}
                style={{
                  position: "relative",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: isMobile ? 10 : 16,
                  padding: isMobile ? "10px 12px" : "14px 20px",
                  borderBottom:
                    "1px solid var(--color-border-light, rgba(0,0,0,0.06))",
                  cursor: showOptionsMenu ? "default" : "pointer",
                  transition: "background 0.1s",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.background =
                    "var(--color-surface-hover)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.background =
                    "transparent";
                }}
              >
                <AgentEffect resourceType="place" objectId={place.id} />
                <div
                  style={{
                    width: isMobile ? 90 : 110,
                    height: isMobile ? 64 : 78,
                    borderRadius: 6,
                    overflow: "hidden",
                    flexShrink: 0,
                    background: "var(--color-surface-hover)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {photoUrl ? (
                    <PlacePhoto url={photoUrl} />
                  ) : (
                    <PlacePhotoPlaceholder />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 500,
                      color: "var(--color-text)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {place.title || place.name || "(Untitled place)"}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginTop: 3,
                    }}
                  >
                    {(() => {
                      const folderName = findPlaceFolderLabel(
                        place,
                        placeFolders,
                      );
                      return folderName ? (
                        <span
                          className="place-list-collection-badge"
                          style={{
                            fontSize: 11,
                            fontWeight: 500,
                            color: "var(--color-brand)",
                            background: "var(--color-brand-muted, #e0f2fe)",
                            padding: "1px 6px",
                            borderRadius: 4,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          {folderName}
                        </span>
                      ) : null;
                    })()}
                    {shortAddress && (
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 400,
                          color: "var(--color-text-secondary, #888)",
                        }}
                      >
                        {shortAddress}
                      </span>
                    )}
                  </div>
                  {userNotes && (
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 400,
                        color: "var(--color-text-secondary, #636871)",
                        marginTop: 4,
                        fontStyle: "italic",
                        lineHeight: 1.4,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical" as const,
                        overflow: "hidden",
                      }}
                    >
                      {userNotes}
                    </div>
                  )}
                </div>
                <div
                  style={{
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: isMobile ? 4 : 8,
                  }}
                >
                  {!isMobile && (
                    <div style={{ textAlign: "center", minWidth: 36 }}>
                      {place.userRating ? (
                        <>
                          <div
                            style={{
                              fontSize: 15,
                              fontWeight: 500,
                              color: "var(--color-text)",
                              fontFamily: "'DM Sans', sans-serif",
                            }}
                          >
                            {place.userRating.toFixed(1)}
                          </div>
                          <div
                            style={{
                              width: 24,
                              height: 2,
                              background: "#1a9ba5",
                              borderRadius: 1,
                              margin: "3px auto",
                            }}
                          />
                        </>
                      ) : null}
                      {place.priceLevel && (
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 400,
                            color: "var(--color-text-muted, #999)",
                            marginTop: place.userRating ? 2 : 0,
                          }}
                        >
                          {place.priceLevel}
                        </div>
                      )}
                    </div>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPlaceMenuId(
                        placeMenuId === place.id ? null : place.id,
                      );
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 4,
                      color: "var(--color-text-faint)",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <circle cx="12" cy="5" r="2" />
                      <circle cx="12" cy="12" r="2" />
                      <circle cx="12" cy="19" r="2" />
                    </svg>
                  </button>
                  {placeMenuId === place.id && (
                    <PlaceOptionsMenu
                      place={place}
                      showArchivedPlaces={showArchivedPlaces}
                      onClose={() => setPlaceMenuId(null)}
                      onUnarchive={() => {
                        updatePlace.mutate({
                          id: place.id,
                          etag:
                            place._etag ??
                            String(place.version ?? ""),
                          patch: {
                            status: "",
                            archived_at: null,
                          } as any,
                        });
                        setPlaceMenuId(null);
                      }}
                      onDelete={async () => {
                        if (
                          await confirmDialog({
                            message: `Permanently delete "${place.title || place.name || "this place"}"? This cannot be undone.`,
                            confirmLabel: "Delete",
                            danger: true,
                          })
                        ) {
                          handleDeletePlace(place.id);
                        }
                        setPlaceMenuId(null);
                      }}
                      onAddToCollection={() => {
                        setAddToCollectionPlaceId(place.id);
                        setPlaceMenuId(null);
                      }}
                      onArchive={() => {
                        updatePlace.mutate({
                          id: place.id,
                          etag:
                            place._etag ??
                            String(place.version ?? ""),
                          patch: {
                            status: "archived",
                            archived_at: new Date().toISOString(),
                          } as any,
                        });
                        setPlaceMenuId(null);
                      }}
                    />
                  )}
                </div>
              </div>
            );
          })}
      </div>

  );
}
